import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthService } from "../services/auth.service";
import { OrganisationService } from "../services/organisation.service";
import MainLayout from "../components/layouts/MainLayout";
import { Select } from "../components/ui/Select";
import claimCrawlerImg from "../assets/images/crawler.jpg";
import dashboardImg from "../assets/images/dashboard.jpg";
import aiVoiceImg from "../assets/images/ai voice agent.jpg";

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [isMessagesCollapsed, setIsMessagesCollapsed] = useState(false);
  const [isNewsCollapsed, setIsNewsCollapsed] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Fetch organisation data
  const { data: organisationData } = useQuery({
    queryKey: ["organisation-data"],
    queryFn: async () => {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser?.organisationName) {
        return await OrganisationService.getOrganisation(currentUser.organisationName);
      }
      return null;
    },
    enabled: !!user?.organisationName,
  });

  const handleSubscribe = () => {
    navigate("/subscription");
  };

  // Dummy product data
  const products = [
    {
      id: 1,
      name: "Claim Flow",
      description:
        "Claim Flow is an intelligent automation tool that ends the hassle of portal hopping by securely logging into insurance portals to retrieve the claim information you need. Stop wasting time on tedious tasks and start optimizing your billing process.",
      image: claimCrawlerImg,
      bgColor: "#4786b1",
    },
    {
      id: 2,
      name: "Dashboard",
      description:
        "Dashboard is an AI-powered tool that automatically gathers, analyzes, and presents your essential performance metrics in one simple interface, giving you a 360-degree view of your practice's health. Stop drowning in data and start driving your practice forward with the full picture.",
      image: dashboardImg,
      bgColor: "#4b80aa",
    },
    {
      id: 3,
      name: "Call Pilot",
      description:
        "Introducing the Call Pilot Agent, the revolutionary service that automates time-consuming calls to insurance companies, freeing up your staff and accelerating your revenue cycle.",
      image: aiVoiceImg,
      bgColor: "#487da7",
    },
  ];

  // Filter products based on search query
  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <MainLayout>
      {/* Right side container */}
      <div className="flex-1 min-w-0 md:ml-0 ml-12">
        {/* Account/Search/Cart section - below header, right of sidebar */}
        <div className="bg-[#EFF0F2] border-b-2 border-border-gray">
          <div className="px-2 sm:px-6 lg:px-6 bg-default-99">
            <div className="flex justify-end flex-wrap items-center py-4 gap-3">
              <div className="flex items-center space-x-4">
                {/* Search */}
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search Products"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64 px-4 py-2 text-sm border border-gray-300 focus:outline-none"
                  />
                  <button
                    onClick={() => searchInputRef.current?.focus()}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-text  hover:opacity-70 transition-opacity"
                    tabIndex={-1}
                    type="button"
                  >
                    <svg
                      className="w-4 h-4 text-primary-blue"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>

                {/* Shopping cart - always 0 */}
                <div className="relative">
                  <button
                    className="p-2 text-black hover:text-gray-600 transition-colors cursor-no-drop"
                    tabIndex={-1}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <span className="absolute -top-1 -right-1 bg-primary-blue text-white text-xs w-5 h-5 flex items-center justify-center">
                      0
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="p-4">
          <div className="max-w-screen-xl mx-auto">
            {/* Top sections row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Messages & Announcements */}
              <div className={isMessagesCollapsed ? "" : "bg-white"}>
                <div className="bg-[#6B9FBD] text-white px-4 py-3 flex justify-between items-center">
                  <h2 className="text-base font-semibold">Messages & Announcements</h2>
                  <button
                    onClick={() => setIsMessagesCollapsed(!isMessagesCollapsed)}
                    className="text-white hover:text-gray-200"
                  >
                    <svg
                      className={`w-5 h-5 transform transition-transform ${isMessagesCollapsed ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                </div>
                {!isMessagesCollapsed && (
                  <div className="p-4">
                    <ul className="space-y-2">
                      <li className="flex items-start text-sm font-medium">
                        <span className="mr-2">▸</span>
                        <span>
                          Claim Flow functionality is live.{" "}
                          <button
                            onClick={() => navigate("/claims")}
                            className="text-link-blue hover:underline"
                          >
                            Try now!
                          </button>
                        </span>
                      </li>
                      <li className="flex items-start text-sm font-medium">
                        <span className="mr-2">▸</span>
                        <span>
                          Dashboard functionality is live.{" "}
                          <button
                            onClick={() => navigate("/dashboards")}
                            className="text-link-blue hover:underline"
                          >
                            Try now!
                          </button>
                        </span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* News */}
              <div className={isNewsCollapsed ? "" : "bg-white"}>
                <div className="bg-[#6B9FBD] text-white px-4 py-3 flex justify-between items-center">
                  <h2 className="text-base font-semibold">News</h2>
                  <button
                    onClick={() => setIsNewsCollapsed(!isNewsCollapsed)}
                    className="text-white hover:text-gray-200"
                  >
                    <svg
                      className={`w-5 h-5 transform transition-transform ${isNewsCollapsed ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                </div>
                {!isNewsCollapsed && (
                  <div className="p-4 space-y-3">
                    {/* Article 1 */}
                    <article className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-sm leading-tight flex-1">
                          Medicare Open Enrollment: 3 Key Changes to Costs in 2026
                        </h3>
                        <span className="text-sm text-gray-500 whitespace-nowrap">Oct 17</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-snug mb-2 line-clamp-2">
                        Medicare premiums and out-of-pocket expenses are changing for 2026. Experts
                        highlight key modifications affecting healthcare coverage costs.
                      </p>
                      <a
                        href="https://www.medicalnewstoday.com/articles/medicare-costs-3-key-changes-in-2026"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-link-blue hover:underline inline-flex items-center gap-1"
                      >
                        Read more
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </article>

                    {/* Article 2 */}
                    <article className="pb-3 last:pb-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-sm leading-tight flex-1">
                          Medicare Open Enrollment 2026: Changes to Part D, Medicare Advantage
                        </h3>
                        <span className="text-sm text-gray-500 whitespace-nowrap">Oct 31</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-snug mb-2 line-clamp-2">
                        2026 enrollment brings modifications to premiums and out-of-pocket costs for
                        Part D and Medicare Advantage plans.
                      </p>
                      <a
                        href="https://www.medicalnewstoday.com/articles/medicare-open-enrollment-aep-2026-premiums-part-d-advantage-plan-changes"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-link-blue hover:underline inline-flex items-center gap-1"
                      >
                        Read more
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </article>
                  </div>
                )}
              </div>
            </div>

            {/* Products section */}
            <div>
              <h2 className="text-xl font-bold mb-3">Products:</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="bg-white overflow-hidden flex flex-col">
                    {/* Product image */}
                    <div
                      className="h-36 flex items-center justify-center p-6"
                      style={{ backgroundColor: product.bgColor }}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    {/* Product info */}
                    <div className="p-4 flex flex-col flex-grow">
                      <h3 className="font-bold text-lg mb-2">{product.name}</h3>
                      <p className="text-sm text-gray-700 mb-4">{product.description}</p>
                      <button
                        onClick={handleSubscribe}
                        className="w-full bg-[#6B9FBD] text-white py-3 hover:bg-[#5a8aa8] transition-colors font-medium mt-auto"
                      >
                        Subscribe
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {filteredProducts.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No products found matching your search.
                </p>
              )}
            </div>
          </div>
        </main>
      </div>
    </MainLayout>
  );
};
